import { useCallback, useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import { FiClipboard, FiEdit2, FiPlus, FiSearch, FiTrash2 } from "react-icons/fi";

import Overlay from "../../../Componentes/Overlay";
import Modal from "../../../Componentes/Utils/Modal";
import { useModal } from "../../../contexts/ModalContext";
import useApi from "../../../hooks/useApi";

import ModalNovoItem from "./ModalNovoItem";
import ModalDeleteItem from "./ModalDeleteItem";

const Container = styled.div`
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: 14px;
    box-sizing: border-box;
`;

const TabelaScroll = styled.div`
    width: 100%;
    min-width: 0;
    max-height: min(480px, calc(90dvh - 260px));
    overflow-x: auto;
    overflow-y: auto;
    border: 1px solid #e2e8f0;
    border-radius: 10px;
    background-color: #fff;
    box-sizing: border-box;

    &::-webkit-scrollbar {
        width: 8px;
        height: 8px;
    }

    &::-webkit-scrollbar-thumb {
        background-color: #cbd5e1;
        border-radius: 4px;
    }

    &::-webkit-scrollbar-track {
        background-color: #f8fafc;
    }
`;

const Header = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    flex-wrap: wrap;
`;

const Titulo = styled.h2`
    margin: 0;
    font-size: 18px;
    color: #263b4b;
    display: flex;
    align-items: center;
    gap: 8px;

    svg {
        color: #263b4b;
    }
`;

const FiltroBox = styled.div`
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: 6px;
`;

const FiltroLabel = styled.label`
    margin: 0;
    padding-left: 2px;
    font-size: 12px;
    font-weight: 700;
    color: #263b4b;
    letter-spacing: 0.02em;
`;

const FiltroInputWrap = styled.div`
    position: relative;
    width: 100%;

    svg {
        position: absolute;
        left: 14px;
        top: 50%;
        transform: translateY(-50%);
        color: #64748b;
        pointer-events: none;
    }
`;

const FiltroInput = styled.input`
    width: 100%;
    height: 44px;
    box-sizing: border-box;
    padding: 0 14px 0 42px;
    border: 1px solid #e2e8f0;
    border-radius: 10px;
    font-size: 13px;
    font-family: inherit;
    color: #1e293b;
    background-color: #f8fafc;
    transition: border-color 0.2s ease, box-shadow 0.2s ease, background-color 0.2s ease;

    &::placeholder {
        color: #94a3b8;
        font-size: 13px;
    }

    &:hover {
        border-color: #cbd5e1;
        background-color: #fff;
    }

    &:focus {
        outline: none;
        border-color: #263b4b;
        background-color: #fff;
        box-shadow: 0 0 0 3px rgba(38, 59, 75, 0.12);
    }
`;

const BotaoNovo = styled.button`
    all: unset;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    background-color: #263b4b;
    color: #fff;
    padding: 8px 14px;
    border-radius: 8px;
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    transition: background-color 0.15s ease, transform 0.15s ease;

    &:hover {
        background-color: #1e2f3c;
        transform: translateY(-1px);
    }
`;

const Tabela = styled.table`
    width: 100%;
    border-collapse: collapse;

    th,
    td {
        padding: 11px 14px;
        font-size: 13px;
        text-align: left;
        border-bottom: 1px solid #e2e8f0;
    }

    thead th {
        position: sticky;
        top: 0;
        z-index: 1;
        background-color: #f1f5f9;
        color: #263b4b;
        font-weight: 700;
        box-shadow: 0 1px 0 #e2e8f0;
    }

    tbody tr:last-child td {
        border-bottom: none;
    }

    tbody tr:hover td {
        background-color: #f8fafc;
    }

    th.ordem,
    td.ordem,
    th.pontos,
    td.pontos {
        width: 80px;
        text-align: center;
    }

    td.acoes {
        width: 96px;
        text-align: center;
    }
`;

const AcoesCelula = styled.div`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
`;

const BotaoAcao = styled.button`
    all: unset;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    border-radius: 6px;
    cursor: pointer;
    color: ${(p) => (p.$danger ? "#b91c1c" : "#1d4ed8")};

    &:hover {
        background-color: ${(p) =>
            p.$danger ? "rgba(185, 28, 28, 0.12)" : "rgba(29, 78, 216, 0.12)"};
    }
`;

const Vazio = styled.div`
    padding: 28px 16px;
    text-align: center;
    color: #64748b;
    font-size: 13px;
    border: 1px dashed #cbd5e1;
    border-radius: 10px;
    background-color: #f8fafc;
`;

const Carregando = styled(Vazio)`
    color: #475569;
`;

const ModalGerenciarItens = ({ tipo }) => {
    const { closeModal, openModal, isModalOpen } = useModal();
    const api = useApi();

    const [itens, setItens] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filtroDescricao, setFiltroDescricao] = useState("");
    const [itemSelecionado, setItemSelecionado] = useState(null);

    const tipoId = tipo?.id;

    const fetchItens = useCallback(async () => {
        if (!tipoId) return;

        setLoading(true);
        try {
            const { data } = await api.get(
                `/Painel/API/Prossmap/Itens/List/index.php?tipo_id=${tipoId}`
            );
            const lista = data?.data ?? data;
            setItens(Array.isArray(lista) ? lista : []);
        } catch (error) {
            console.error("Erro ao listar itens:", error);
            setItens([]);
        } finally {
            setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tipoId]);

    useEffect(() => {
        fetchItens();
    }, [fetchItens]);

    const itensFiltrados = useMemo(() => {
        const termo = filtroDescricao.trim().toLowerCase();
        if (!termo) return itens;
        return itens.filter((i) =>
            String(i.descricao ?? "").toLowerCase().includes(termo)
        );
    }, [itens, filtroDescricao]);

    const handleAbrirNovo = () => {
        setItemSelecionado(null);
        openModal("prossmapNovoItem");
    };

    const handleAbrirEdicao = (item) => {
        setItemSelecionado(item);
        openModal("prossmapNovoItem");
    };

    const handleAbrirDelete = (item) => {
        setItemSelecionado(item);
        openModal("prossmapDeleteItem");
    };

    return (
        <>
            <Overlay state>
                <Modal header close={closeModal} size="lg">
                    <Container>
                        <Header>
                            <Titulo>
                                <FiClipboard size={20} />
                                ITENS — {tipo?.codigo ? `${tipo.codigo} — ` : ""}
                                {tipo?.nome ?? ""}
                            </Titulo>
                            <BotaoNovo type="button" onClick={handleAbrirNovo}>
                                <FiPlus size={16} />
                                NOVO ITEM
                            </BotaoNovo>
                        </Header>

                        <FiltroBox>
                            <FiltroLabel htmlFor="filtro-item-descricao">
                                FILTRAR POR DESCRIÇÃO
                            </FiltroLabel>
                            <FiltroInputWrap>
                                <FiSearch size={16} aria-hidden />
                                <FiltroInput
                                    id="filtro-item-descricao"
                                    type="text"
                                    placeholder="Digite para buscar na lista…"
                                    value={filtroDescricao}
                                    onChange={(e) =>
                                        setFiltroDescricao(e.target.value.toUpperCase())
                                    }
                                />
                            </FiltroInputWrap>
                        </FiltroBox>

                        {loading ? (
                            <Carregando>Carregando itens…</Carregando>
                        ) : itensFiltrados.length === 0 ? (
                            <Vazio>
                                {itens.length === 0
                                    ? "Nenhum item cadastrado. Clique em NOVO ITEM para começar."
                                    : "Nenhum item encontrado para o filtro informado."}
                            </Vazio>
                        ) : (
                            <TabelaScroll>
                                <Tabela>
                                    <thead>
                                        <tr>
                                            <th className="ordem">ORDEM</th>
                                            <th>DESCRIÇÃO</th>
                                            <th className="pontos">PONTOS</th>
                                            <th className="acoes">AÇÕES</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {itensFiltrados.map((item) => (
                                            <tr key={item.id}>
                                                <td className="ordem">{item.ordem}</td>
                                                <td>{item.descricao}</td>
                                                <td className="pontos">{item.pontos}</td>
                                                <td className="acoes">
                                                    <AcoesCelula>
                                                        <BotaoAcao
                                                            type="button"
                                                            title="Editar item"
                                                            aria-label="Editar item"
                                                            onClick={() => handleAbrirEdicao(item)}
                                                        >
                                                            <FiEdit2 size={15} />
                                                        </BotaoAcao>
                                                        <BotaoAcao
                                                            $danger
                                                            type="button"
                                                            title="Remover item"
                                                            aria-label="Remover item"
                                                            onClick={() => handleAbrirDelete(item)}
                                                        >
                                                            <FiTrash2 size={15} />
                                                        </BotaoAcao>
                                                    </AcoesCelula>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Tabela>
                            </TabelaScroll>
                        )}
                    </Container>
                </Modal>
            </Overlay>

            {isModalOpen("prossmapNovoItem") && (
                <ModalNovoItem
                    tipoId={tipoId}
                    item={itemSelecionado}
                    onSaved={fetchItens}
                    key={itemSelecionado?.id ?? "novo"}
                />
            )}

            {isModalOpen("prossmapDeleteItem") && (
                <ModalDeleteItem
                    item={itemSelecionado}
                    fecharModal={closeModal}
                    onDeleted={fetchItens}
                />
            )}
        </>
    );
};

export default ModalGerenciarItens;
